import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Image,
  Text,
  VStack,
  HStack,
  Avatar,
  AvatarBadge,
  FormControl,
  FormLabel,
  Input,
  Divider,
  useToast,
  Badge,
  ScaleFade,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import { COLORS, GRADIENTS, ROUTES } from '../utils/constants';
import useAuth from '../store/useAuth';
import useCredits from '../store/useCredits';

function Profile() {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [userData, setUserData] = useState({
    name: 'John Doe',
    email: 'john.doe@example.com',
    walletAddress: 'r9LpS5ZzPMNVvucKCiPfRbvMZ4uVRdqP3E',
    joinDate: '2023-05-15',
    username: 'johndoe',
    bio: 'AI enthusiast and crypto developer'
  });
  
  const navigate = useNavigate();
  const toast = useToast();
  
  const { user, isLoggedIn, logout } = useAuth();
  const { balance } = useCredits();
  
  // Animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);
  
  // Redirect to login if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
    }
  }, [isLoggedIn, navigate]);
  
  // Usage statistics
  const usageStats = {
    totalImagesGenerated: 157,
    totalCreditsSpent: 235,
    totalXgcSpent: 94,
    favoriteStyles: ['Cyberpunk', 'Abstract', 'Photorealistic'],
    lastGeneration: '2 hours ago'
  };
  
  // Update user data with actual user info when available
  useEffect(() => {
    if (user) {
      setUserData(prev => ({
        ...prev,
        name: user.displayName || user.username || prev.name,
        email: user.email || prev.email,
        username: user.username || prev.username,
        walletAddress: user.walletAddress || prev.walletAddress,
        bio: user.bio || prev.bio
      }));
    }
  }, [user]);
  
  // Mock transaction history
  const mockTransactions = [
    { id: 'tx1', date: '2023-07-15', type: 'Purchase', amount: '50 Credits', xgc: '20 XGCL' },
    { id: 'tx2', date: '2023-07-02', type: 'Purchase', amount: '20 Credits', xgc: '8 XGCL' },
    { id: 'tx3', date: '2023-06-28', type: 'Purchase', amount: '10 Credits', xgc: '5 XGCL' },
    { id: 'tx4', date: '2023-06-15', type: 'Purchase', amount: '100 Credits', xgc: '40 XGCL' },
  ];
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSaveProfile = async () => {
    setIsLoading(true);
    
    try {
      // Simulate API call to update user profile
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In a real app, this would call your backend API
      
      toast({
        title: 'Profile updated',
        description: 'Your profile has been successfully updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      setIsEditing(false);
    } catch (error) {
      toast({
        title: 'Update failed',
        description: error.message || 'Failed to update profile',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleLogout = () => {
    logout();
    toast({
      title: 'Logged out successfully',
      description: 'You have been logged out of your account',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    navigate('/login');
  };
  
  return (
    <Container maxW="4xl" py={8}>
      <ScaleFade in={isVisible} initialScale={0.95}>
        <VStack spacing={8} align="stretch">
          <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
            <Box>
              <Flex align="center" mb={2}>
                <Image 
                  src="/IgnitaLogo.svg" 
                  alt="Ignita Logo" 
                  height="40px"
                  mr={3}
                />
                <Heading 
                  as="h1" 
                  size="xl" 
                  bgGradient={GRADIENTS.primary}
                  bgClip="text"
                  className="glow-text"
                  letterSpacing="tight"
                  fontFamily="Space Grotesk"
                >
                  My Profile
                </Heading>
              </Flex>
              <Text color={COLORS.textSecondary}>
                Manage your account and view your activity
              </Text>
            </Box>
            
            <Badge 
              px={4} 
              py={2} 
              borderRadius="md" 
              fontWeight="bold"
              bg="rgba(0, 168, 107, 0.15)"
              border="1px solid"
              borderColor={COLORS.primary}
              color={COLORS.accent}
              className="pulse"
            >
              Credits: {balance}
            </Badge>
          </Flex>
          
          <Tabs variant="soft-rounded" colorScheme="green">
            <TabList mb={6}>
              <Tab 
                color={COLORS.textSecondary} 
                _selected={{ 
                  color: COLORS.text, 
                  bg: "rgba(0, 168, 107, 0.15)",
                  fontWeight: "bold"
                }}
              >
                Profile
              </Tab>
              <Tab 
                color={COLORS.textSecondary} 
                _selected={{ 
                  color: COLORS.text, 
                  bg: "rgba(0, 168, 107, 0.15)",
                  fontWeight: "bold"
                }}
              >
                Statistics
              </Tab>
              <Tab 
                color={COLORS.textSecondary} 
                _selected={{ 
                  color: COLORS.text, 
                  bg: "rgba(0, 168, 107, 0.15)",
                  fontWeight: "bold"
                }}
              >
                Transactions
              </Tab>
            </TabList>
            
            <TabPanels>
              {/* Profile Tab */}
              <TabPanel p={0}>
                <Box 
                  bg="rgba(0, 0, 0, 0.6)" 
                  p={6} 
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={COLORS.primary}
                  className="glow-border"
                  boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
                  backdropFilter="blur(10px)"
                >
                  <Flex 
                    direction={{ base: "column", md: "row" }} 
                    gap={8}
                    align={{ base: "center", md: "flex-start" }}
                  >
                    <Box textAlign="center">
                      <Avatar 
                        size="2xl" 
                        name={userData.name} 
                        bg={COLORS.primary}
                        color="black"
                        fontWeight="bold"
                        src=""
                        border="2px solid"
                        borderColor={COLORS.accent}
                        className="glow-border"
                      >
                        <AvatarBadge 
                          boxSize="1.25em" 
                          bg={COLORS.accent} 
                          border="2px solid"
                          borderColor={COLORS.background}
                        />
                      </Avatar>
                      
                      {!isEditing && (
                        <Button
                          size="sm"
                          className="outline-button"
                          mt={4}
                        >
                          Change Photo
                        </Button>
                      )}
                    </Box>
                    
                    <Box flex="1">
                      {isEditing ? (
                        <VStack spacing={4} align="stretch">
                          <Flex gap={4} direction={{ base: "column", md: "row" }}>
                            <FormControl flex="1">
                              <FormLabel color={COLORS.textSecondary}>Full Name</FormLabel>
                              <Input
                                name="name"
                                value={userData.name}
                                onChange={handleInputChange}
                                bg="rgba(0, 0, 0, 0.2)"
                                border="1px solid"
                                borderColor="rgba(30, 220, 11, 0.3)"
                                _hover={{ borderColor: COLORS.accent }}
                                _focus={{ 
                                  borderColor: COLORS.accent,
                                  boxShadow: `0 0 0 1px ${COLORS.accent}`
                                }}
                                color={COLORS.text}
                              />
                            </FormControl>
                            
                            <FormControl flex="1">
                              <FormLabel color={COLORS.textSecondary}>Username</FormLabel>
                              <Input
                                name="username"
                                value={userData.username}
                                onChange={handleInputChange}
                                bg="rgba(0, 0, 0, 0.2)"
                                border="1px solid"
                                borderColor="rgba(30, 220, 11, 0.3)"
                                _hover={{ borderColor: COLORS.accent }}
                                _focus={{ 
                                  borderColor: COLORS.accent,
                                  boxShadow: `0 0 0 1px ${COLORS.accent}`
                                }}
                                color={COLORS.text}
                              />
                            </FormControl>
                          </Flex>
                          
                          <FormControl>
                            <FormLabel color={COLORS.textSecondary}>Email</FormLabel>
                            <Input
                              name="email"
                              value={userData.email}
                              onChange={handleInputChange}
                              bg="rgba(0, 0, 0, 0.2)"
                              border="1px solid"
                              borderColor="rgba(30, 220, 11, 0.3)"
                              _hover={{ borderColor: COLORS.accent }}
                              _focus={{ 
                                borderColor: COLORS.accent,
                                boxShadow: `0 0 0 1px ${COLORS.accent}`
                              }}
                              color={COLORS.text}
                            />
                          </FormControl>
                          
                          <FormControl>
                            <FormLabel color={COLORS.textSecondary}>Bio</FormLabel>
                            <Input
                              name="bio"
                              value={userData.bio}
                              onChange={handleInputChange}
                              bg="rgba(0, 0, 0, 0.2)"
                              border="1px solid"
                              borderColor="rgba(30, 220, 11, 0.3)"
                              _hover={{ borderColor: COLORS.accent }}
                              _focus={{ 
                                borderColor: COLORS.accent,
                                boxShadow: `0 0 0 1px ${COLORS.accent}`
                              }}
                              color={COLORS.text}
                            />
                          </FormControl>
                          
                          <Divider borderColor="rgba(30, 220, 11, 0.3)" />
                          
                          <HStack>
                            <Button
                              onClick={handleSaveProfile}
                              isLoading={isLoading}
                              loadingText="Saving..."
                              className="primary-button"
                            >
                              Save Changes
                            </Button>
                            
                            <Button
                              onClick={() => setIsEditing(false)}
                              className="outline-button"
                            >
                              Cancel
                            </Button>
                          </HStack>
                        </VStack>
                      ) : (
                        <VStack align="stretch" spacing={4}>
                          <Box>
                            <Heading size="lg" color={COLORS.text}>{userData.name}</Heading>
                            <Text color={COLORS.textSecondary}>@{userData.username}</Text>
                          </Box>
                          
                          <Text color={COLORS.text}>{userData.bio}</Text>
                          
                          <Divider borderColor="rgba(30, 220, 11, 0.3)" />
                          
                          <Box>
                            <Text color={COLORS.textSecondary} fontSize="sm">Email</Text>
                            <Text color={COLORS.text}>{userData.email}</Text>
                          </Box>
                          
                          <Box>
                            <Text color={COLORS.textSecondary} fontSize="sm">XAMAN Wallet</Text>
                            <Text color={COLORS.text} fontFamily="monospace">
                              {userData.walletAddress.slice(0, 8)}...{userData.walletAddress.slice(-8)}
                            </Text>
                          </Box>
                          
                          <Box>
                            <Text color={COLORS.textSecondary} fontSize="sm">Member Since</Text>
                            <Text color={COLORS.text}>{new Date(userData.joinDate).toLocaleDateString()}</Text>
                          </Box>
                          
                          <Divider borderColor="rgba(30, 220, 11, 0.3)" />
                          
                          <HStack justify="space-between">
                            <Button
                              onClick={() => setIsEditing(true)}
                              className="primary-button"
                              size="sm"
                            >
                              Edit Profile
                            </Button>
                            
                            <Button
                              onClick={handleLogout}
                              variant="outline"
                              borderColor="red.400"
                              color="red.400"
                              size="sm"
                              _hover={{
                                bg: "rgba(245, 101, 101, 0.1)",
                                borderColor: "red.300",
                                color: "red.300"
                              }}
                            >
                              Logout
                            </Button>
                          </HStack>
                        </VStack>
                      )}
                    </Box>
                  </Flex>
                </Box>
              </TabPanel>
              
              {/* Statistics Tab */}
              <TabPanel p={0}>
                <Box 
                  bg="rgba(0, 0, 0, 0.6)" 
                  p={6} 
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={COLORS.primary}
                  className="glow-border"
                  boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
                  backdropFilter="blur(10px)"
                >
                  <Heading size="md" mb={6} className="glow-text">Usage Statistics</Heading>
                  
                  <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6} mb={8}>
                    <GridItem>
                      <Stat
                        bg="rgba(0, 0, 0, 0.2)"
                        p={4}
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor="rgba(30, 220, 11, 0.3)"
                      >
                        <StatLabel color={COLORS.textSecondary}>Total Images</StatLabel>
                        <StatNumber color={COLORS.text}>{usageStats.totalImagesGenerated}</StatNumber>
                        <StatHelpText color={COLORS.accent}>
                          All time
                        </StatHelpText>
                      </Stat>
                    </GridItem>
                    
                    <GridItem>
                      <Stat
                        bg="rgba(0, 0, 0, 0.2)"
                        p={4}
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor="rgba(30, 220, 11, 0.3)"
                      >
                        <StatLabel color={COLORS.textSecondary}>Credits Used</StatLabel>
                        <StatNumber color={COLORS.text}>{usageStats.totalCreditsSpent}</StatNumber>
                        <StatHelpText color={COLORS.accent}>
                          All time
                        </StatHelpText>
                      </Stat>
                    </GridItem>
                    
                    <GridItem>
                      <Stat
                        bg="rgba(0, 0, 0, 0.2)"
                        p={4}
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor="rgba(30, 220, 11, 0.3)"
                      >
                        <StatLabel color={COLORS.textSecondary}>XGCL Spent</StatLabel>
                        <StatNumber color={COLORS.text}>{usageStats.totalXgcSpent} XGCL</StatNumber>
                        <StatHelpText color={COLORS.accent}>
                          ≈ ${(usageStats.totalXgcSpent * 0.25).toFixed(2)} USD
                        </StatHelpText>
                      </Stat>
                    </GridItem>
                  </Grid>
                  
                  <Box mb={6}>
                    <Text fontWeight="medium" color={COLORS.text} mb={2}>Favorite Styles</Text>
                    <HStack spacing={2}>
                      {usageStats.favoriteStyles.map((style) => (
                        <Badge 
                          key={style}
                          px={3}
                          py={1}
                          borderRadius="full"
                          bg="rgba(30, 220, 11, 0.15)"
                          color={COLORS.text}
                          fontWeight="medium"
                        >
                          {style}
                        </Badge>
                      ))}
                    </HStack>
                  </Box>
                  
                  <Divider borderColor="rgba(30, 220, 11, 0.3)" my={6} />
                  
                  <Flex justify="space-between" align="center">
                    <Box>
                      <Text fontWeight="medium" color={COLORS.text}>Last Generation</Text>
                      <Text color={COLORS.textSecondary}>{usageStats.lastGeneration}</Text>
                    </Box>
                    
                    <Button
                      onClick={() => navigate(ROUTES.GENERATE)}
                      variant="outline"
                      borderColor={COLORS.primary}
                      color={COLORS.primary}
                      _hover={{
                        bg: "rgba(0, 168, 107, 0.1)",
                        borderColor: COLORS.accent,
                        color: COLORS.accent
                      }}
                    >
                      Generate New Image
                    </Button>
                  </Flex>
                </Box>
              </TabPanel>
              
              {/* Transactions Tab */}
              <TabPanel p={0}>
                <Box 
                  bg="rgba(0, 0, 0, 0.6)" 
                  p={6} 
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={COLORS.primary}
                  className="glow-border"
                  boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
                  backdropFilter="blur(10px)"
                >
                  <Flex justify="space-between" align="center" mb={6}>
                    <Heading size="md" className="glow-text">Transaction History</Heading>
                    
                    <Button
                      size="sm"
                      onClick={() => navigate('/buy-credits')}
                      variant="outline"
                      borderColor={COLORS.primary}
                      color={COLORS.primary}
                      _hover={{
                        bg: "rgba(0, 168, 107, 0.1)",
                        borderColor: COLORS.accent,
                        color: COLORS.accent
                      }}
                    >
                      Buy More Credits
                    </Button>
                  </Flex>
                  
                  <VStack spacing={4} align="stretch">
                    {mockTransactions.map((tx) => (
                      <Flex 
                        key={tx.id}
                        justify="space-between"
                        align="center"
                        p={4}
                        bg="rgba(0, 0, 0, 0.2)"
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor="rgba(30, 220, 11, 0.2)"
                      >
                        <Box>
                          <Text fontWeight="medium" color={COLORS.text}>{tx.type}</Text>
                          <Text fontSize="sm" color={COLORS.textSecondary}>{tx.date}</Text>
                        </Box>
                        
                        <Box textAlign="right">
                          <Text fontWeight="bold" color={COLORS.accent}>{tx.amount}</Text>
                          <Text fontSize="sm" color={COLORS.textSecondary}>{tx.xgc}</Text>
                        </Box>
                      </Flex>
                    ))}
                  </VStack>
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </ScaleFade>
    </Container>
  );
}

export default Profile; 